import { useMutation, useQuery, useQueryClient } from 'react-query';
import useContext from '../../../hooks/useContext';
import { PostalContext } from '../../../providers/postal';
import { getPreviewHeader } from '../../cart';
import useCart from '../../../providers/cart';
import { get, post, _delete, put } from '../../fetch';
import equal from 'fast-deep-equal';
import useCommerceUrl from '../../useCommerceUrl';
import { DeliveryStrategyType } from '../../../../@types/deliveries.enum';
import { SiteContext } from '../../../SiteContext';
import { appendPreviewDate } from '../../helper';
import { uniq } from 'lodash';

export enum QueryKey {
  DeliveryMethods = 'deliveryMethods',
  Availability = 'availability',
}

// Selection is invalid if strategy is by week, have only 2 buckets and one of them
// only contains a giftcard
const isValidSelection = (strategiesResponse: Deliveries.IShippingStrategiesResponse) => {
  const type = strategiesResponse.deliverySelections.selectedStrategyType;

  if (type === DeliveryStrategyType.AllItems) {
    return true;
  }

  // assumes strategy is ByWeek when not AllItems
  const buckets = strategiesResponse.strategies.shippingStrategies.find(
    (s) => s.type === type,
  )?.shippingBuckets;

  if (buckets?.length !== 2) {
    return true;
  }

  const onlyGiftcardBucket = buckets?.find((s) =>
    s.items.every((i) => i.m3Id?.toLowerCase().startsWith('pre')),
  );

  if (!onlyGiftcardBucket) {
    return true;
  }

  return false;
};

type VariantsResponse = {
  variants: IProduct[];
};

const getVariantsM3Id = async (searchServiceUrl: string, variantIds: string[]) => {
  const url = appendPreviewDate(`${searchServiceUrl}/variants?id=${variantIds.join('&id=')}`);
  const response = await get<VariantsResponse>(url);

  return response.variants.reduce(
    (acc, v) => ({
      ...acc,
      [v.id]: v.m3Id,
    }),
    {} as Record<string, string>,
  );
};

const useAutoSetGiftcardBucketDeliveryMethod = (
  cartId?: string,
  cartKey?: string,
  postal?: string | null,
) => {
  const { data: deliveryStrategies } = useGetDeliveryStrategies(cartId, cartKey, postal, true);
  const setGiftcardBucketMethod = useAutoSetGiftcardBucketMethod(cartId, cartKey, postal);
  return async <T extends Deliveries.ISetDeliverySelectionResponse>(response: T) => {
    return await setGiftcardBucketMethod(response, deliveryStrategies);
  };
};

// This is to prevent buckets with only giftcards from
// being exposed to end user. This function automatically sets
// the first available delivery method for a bucket with only a giftcard.
// Other functionality is responsible for hiding this bucket.
const useAutoSetGiftcardBucketMethod = (
  cartId?: string,
  cartKey?: string,
  postal?: string | null,
) => {
  const {
    data: { searchServiceUrl },
  } = useContext(SiteContext);
  const { mutateAsync } = useSetDeliveryMethod(cartId, cartKey, postal);

  return async <T extends Deliveries.IDeliverySelectionsResponse>(
    response: T,
    deliveryStrategies?: Deliveries.IShippingStrategiesResponse,
  ) => {
    const type = response.deliverySelections.selectedStrategyType;
    if (type === DeliveryStrategyType.AllItems) {
      return response;
    }

    if (!postal) {
      return response;
    }

    // Assumes: Selected strategy type is not AllItems, then it is ByWeek
    const selection = response.deliverySelections.strategySelections['ByWeek'];
    const variantIds = uniq(
      selection?.deliverySelections.reduce(
        (acc, selection) => acc.concat(selection.items),
        [] as string[],
      ),
    );

    const variantM3IdMap = await getVariantsM3Id(searchServiceUrl, variantIds);

    const giftCardBucket = selection?.deliverySelections.find((b) =>
      b.items.every((i) => variantM3IdMap[i]?.toLowerCase().startsWith('pre')),
    );

    if (!giftCardBucket) {
      return response;
    }

    if (giftCardBucket.delivery) {
      return response;
    }

    const strategy = deliveryStrategies?.strategies.shippingStrategies.find((s) => s.type === type);

    const delivery = strategy?.shippingBuckets.find((b) => b.id === giftCardBucket.shippingBucketId)
      ?.deliveries?.[0];

    if (!delivery) {
      return response;
    }

    return await mutateAsync({
      id: delivery.id,
      type: delivery.type,
      location: { id: delivery.locations[0].id },
      bucketId: giftCardBucket.shippingBucketId,
      postalCode: postal,
    });
  };
};

const useGetDeliveryStrategies = (
  cartId?: string,
  cartKey?: string,
  postal?: string | null,
  usePostal?: boolean,
) => {
  const { commerceUrl } = useCommerceUrl('latest');

  const url = postal
    ? `${commerceUrl}/delivery/${cartId}?postalCode=${postal}`
    : `${commerceUrl}/delivery/${cartId}`;

  const autoSet = useAutoSetGiftcardBucketMethod(cartId, cartKey, postal);
  const { mutateAsync } = useSetSelectedStrategyMutation(cartId, cartKey, postal);

  return useQuery(
    [QueryKey.DeliveryMethods, cartKey, postal],
    () =>
      get<Deliveries.IShippingStrategiesResponse>(url, getPreviewHeader()).then((resp) => {
        if (isValidSelection(resp)) {
          return autoSet(resp, resp).then(() => resp);
        }

        return mutateAsync({
          deliveryStrategyType: DeliveryStrategyType.AllItems,
        }).then(() => {
          window.location.reload();
          return resp;
        });
      }),
    {
      enabled: !!cartId && (!usePostal || (usePostal && !!postal)),
    },
  );
};

export const useGetAvailability = (cartId?: string, cartKey?: string) => {
  const { commerceUrl } = useCommerceUrl();

  return useQuery(
    [QueryKey.Availability, cartKey],
    () =>
      get<Commerce.IUnavailableItems>(
        `${commerceUrl}/delivery/${cartId}/availability`,
        getPreviewHeader(),
      ),
    {
      enabled: !!cartId,
    },
  );
};

const useSetDeliveryMethod = (cartId?: string, cartKey?: string, postal?: string | null) => {
  const queryClient = useQueryClient();
  const { commerceUrl } = useCommerceUrl();

  return useMutation<
    Deliveries.ISetDeliverySelectionResponse,
    Commerce.CommerceException,
    Deliveries.IDeliveryMethodRequest
  >(
    (request: Deliveries.IDeliveryMethodRequest) =>
      post<Deliveries.ISetDeliverySelectionResponse, Deliveries.IDeliveryMethodRequest>(
        `${commerceUrl}/delivery/${cartId}`,
        request,
        getPreviewHeader(),
      ),
    {
      onSuccess: (data) => {
        queryClient.setQueryData([QueryKey.Availability, cartKey], data.unavailableItems);
        queryClient.setQueryData(
          [QueryKey.DeliveryMethods, cartKey, postal],
          // @ts-ignore
          (oldData) => ({ ...oldData, deliverySelections: data.deliverySelections }),
        );
      },
    },
  );
};

const useSetSelectedStrategy = (cartId?: string, cartKey?: string, postal?: string | null) => {
  const onStrategySet = useAutoSetGiftcardBucketDeliveryMethod(cartId, cartKey, postal);
  return useSetSelectedStrategyMutation(cartId, cartKey, postal, onStrategySet);
};

type StrategySetCallback = (
  response: Deliveries.ISetDeliverySelectionResponse,
) => Promise<Deliveries.ISetDeliverySelectionResponse>;

const useSetSelectedStrategyMutation = (
  cartId?: string,
  cartKey?: string,
  postal?: string | null,
  onStrategySet?: StrategySetCallback,
) => {
  const queryClient = useQueryClient();
  const { commerceUrl } = useCommerceUrl();

  return useMutation<
    Deliveries.ISetDeliverySelectionResponse,
    Commerce.CommerceException,
    Deliveries.IDeliveryStrategyRequest
  >(
    (request: Deliveries.IDeliveryStrategyRequest) =>
      put<Deliveries.ISetDeliverySelectionResponse, Deliveries.IDeliveryStrategyRequest>(
        `${commerceUrl}/delivery/${cartId}/strategy`,
        request,
        getPreviewHeader(),
      ).then((resp) => {
        if (onStrategySet) {
          return onStrategySet(resp);
        }

        return resp;
      }),
    {
      onSuccess: (data) => {
        queryClient.setQueryData([QueryKey.Availability, cartKey], data.unavailableItems);
        queryClient.setQueryData(
          [QueryKey.DeliveryMethods, cartKey, postal],
          // @ts-ignore
          (oldData) => ({ ...oldData, deliverySelections: data.deliverySelections }),
        );
      },
    },
  );
};

export const useRemoveDelivery = (cartId?: string) => {
  const { commerceUrl } = useCommerceUrl();

  return useMutation(() =>
    _delete<Deliveries.IDeliverySelectionsResponse>(
      `${commerceUrl}/delivery/${cartId}`,
      getPreviewHeader(),
    ),
  );
};

const useSetAssemblyItems = (cartId?: string, cartKey?: string, postal?: string | null) => {
  const queryClient = useQueryClient();
  const { commerceUrl } = useCommerceUrl('latest');

  return useMutation<
    Deliveries.ISetDeliverySelectionResponse,
    Commerce.CommerceException,
    Deliveries.IDeliveryAssemblyRequest
  >({
    mutationFn: (request: any) =>
      post<Deliveries.ISetDeliverySelectionResponse, Deliveries.IDeliveryAssemblyRequest>(
        `${commerceUrl}/assembly/${cartId}`,
        request.body,
        getPreviewHeader(),
      ),
    onMutate: async (request) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [QueryKey.DeliveryMethods, cartKey, postal] });

      // Snapshot the previous value
      const previous = queryClient.getQueryData([QueryKey.DeliveryMethods, cartKey, postal]);

      // Optimistically update to the new value
      const updated = queryClient.setQueryData(
        [QueryKey.DeliveryMethods, cartKey, postal],
        (current: any) => {
          return {
            ...current,
            deliverySelections: {
              ...current.deliverySelections,
              strategySelections: request.newStrategySelections,
            },
          };
        },
      );

      // Return a context object with the snapshotted value
      return { previous, updated };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (_err, _request, context) => {
      queryClient.setQueryData(
        [QueryKey.DeliveryMethods, cartKey, postal],
        // @ts-ignore
        context.previous,
      );
    },
    onSuccess: async (requestResponseData, _request, context) => {
      // @ts-ignore
      const { updated: optimisticData } = context;
      const { optimisticDeliverySelections } = optimisticData;
      const { deliverySelections: responseDeliverySelections } = requestResponseData;

      const currentKeyData = queryClient.getQueryData([QueryKey.DeliveryMethods, cartKey, postal]);

      // Current data for this key is the same as when we started this
      // mutation. Meaining no other mutation or query has been made inbetween.
      const keyNotMutated = equal(currentKeyData, optimisticData);

      // Checks if the guess of how the data should be optimisticly updated
      // is what actually got returned from the request in the background.
      const correctGuess = equal(optimisticDeliverySelections, responseDeliverySelections);

      // Checks if the key have been mutated and if the optimistic guess is correct.
      // If our guess was not correct we uptade the key data to the response.
      // If the key have been mutated this mutation is abandoned.
      if (keyNotMutated && !correctGuess) {
        queryClient.setQueryData([QueryKey.DeliveryMethods, cartKey, postal], {
          // @ts-ignore
          ...currentKeyData,
          deliverySelections: responseDeliverySelections,
        });
      }
    },
  });
};

export const useDeliveryStrategyApi = (usePostal: boolean) => {
  const { cart, key: cartKey } = useCart();
  const { postal } = useContext(PostalContext);
  const {
    data,
    isLoading: fetching,
    refetch: refetchDeliveryMethods,
    isRefetching,
  } = useGetDeliveryStrategies(cart?.id, cartKey, postal, usePostal);
  const setDeliveryMutation = useSetDeliveryMethod(cart?.id, cartKey, postal);
  const setShippingStrategyMutation = useSetSelectedStrategy(cart?.id, cartKey, postal);
  const deleteDeliveryMutation = useRemoveDelivery(cart?.id);
  const setAssemblyItemsMutation = useSetAssemblyItems(cart?.id, cartKey, postal);

  return {
    deleteDelivery: deleteDeliveryMutation.mutate,
    deletingDelivery: deleteDeliveryMutation.isLoading,
    deliveryStrategies: data?.strategies?.shippingStrategies,
    deliverySelections: data?.deliverySelections,
    availableAssemblyItems: data?.strategies?.assemblyItems || [],
    error: setDeliveryMutation.error,
    fetching,
    isRefetching,
    refetchDeliveryMethods: refetchDeliveryMethods,
    resetError: setDeliveryMutation.reset,
    setDelivery: setDeliveryMutation.mutate,
    setShippingStrategy: setShippingStrategyMutation.mutate,
    setShippingStrategyError: setShippingStrategyMutation.error,
    resetSetShippingStrategyError: setShippingStrategyMutation.reset,
    settingDelivery: setDeliveryMutation.isLoading,
    settingStrategy: setShippingStrategyMutation.isLoading,
    setAssemblyItems: setAssemblyItemsMutation.mutate,
    settingAssemblyItems: setAssemblyItemsMutation.isLoading,
    settingAssemblyItemsError: setAssemblyItemsMutation.isError,
  };
};
